import {useRef} from 'react';

import classnames from 'classnames';

import {EmailEditor} from 'shells/email-editor/email-editor';

import type {
    GetTeams_teams as Team,
    GetSessionUser_loggedInUser_sendableEmailAddresses as UserSendableEmailAddress,
} from 'nutshell-graphql-types';

import {TextareaMentionable} from '../../../../../../../ui/core/textarea-mentionable/textarea-mentionable';

import {CollapsedMessageInputRow} from './collapsed-message-input-row';
import {EmailInputHeader} from './email-input-header';
import {MODE, type Mode, type SenderAddress} from './types';

import collapsedStyles from './collapsed-message-input-row.module.css';
import styles from './inbox-rich-text-input.module.css';

const INBOX_RICH_TEXT_INPUT_ID = 'inbox-rich-text-input';

interface Props {
    value: string;
    onChange: (htmlValue: string, textValue: string) => void;
    placeholder?: string;
    isDisabled?: boolean;
    teams?: Team[];
    footer?: React.ReactNode;
    className?: string;
    selectedSender: SenderAddress | null;
    setSelectedSender: (sender: SenderAddress | null) => void;
    mode: Mode;
    setMode: (mode: Mode) => void;
    signature?: string | null | undefined;
    useSignature: boolean;
    setUseSignature: (value: boolean) => void;
    clearAllImages: () => void;
    userSendableEmailAddresses?: UserSendableEmailAddress[] | null | undefined;
    isExpanded: boolean;
    onExpand: () => void;
    previewText: string;
    collapsedFooter?: React.ReactNode;
}

export const InboxRichTextInput = (props: Props) => {
    const quillRef = useRef<HTMLElement | null | undefined>(null);

    return (
        <div
            className={classnames(styles['inbox-rich-text-input-container'], props.className, {
                [styles['note-mode']]: props.mode === MODE.INTERNAL_NOTE,
                [collapsedStyles['collapsed']]: !props.isExpanded,
            })}
        >
            {props.isExpanded ? (
                <>
                    <EmailInputHeader
                        selectedSender={props.selectedSender}
                        setSelectedSender={props.setSelectedSender}
                        mode={props.mode}
                        setMode={props.setMode}
                        clearAllImages={props.clearAllImages}
                        userSendableEmailAddresses={props.userSendableEmailAddresses}
                    />
                    {props.mode === MODE.EMAIL ? (
                        <EmailEditor
                            value={props.value}
                            onChange={props.onChange}
                            placeholder={props.placeholder}
                            readOnly={props.isDisabled}
                            useSignature={props.useSignature}
                            signature={props.signature}
                            onChangeUseSignature={() => props.setUseSignature(!props.useSignature)}
                            hasExtraSpaceForSendButton={true}
                            isBodyFocused={true}
                        />
                    ) : (
                        <TextareaMentionable
                            id={INBOX_RICH_TEXT_INPUT_ID}
                            getQuillRef={(ref) => (quillRef.current = ref)}
                            placeholder='Type a message'
                            isFullHeight={true}
                            isFullWidth={true}
                            widePadding={true}
                            value={props.value}
                            onChange={props.onChange}
                            onTabPropagateToQuill={true}
                            preloadedTeams={props.teams}
                            overflowVisible={true}
                            isBodyFocused={true}
                        />
                    )}
                    {props.footer}
                </>
            ) : (
                <CollapsedMessageInputRow
                    previewText={props.previewText}
                    placeholder={props.placeholder}
                    onExpand={props.onExpand}
                    footer={props.collapsedFooter ?? props.footer}
                    mode={props.mode}
                />
            )}
        </div>
    );
};
